import React from 'react';
import { Users, UserPlus, UserX, Clock, AlertTriangle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент раздела параметров водителей
 */
const DriverParametersSection = ({ 
  formData, 
  handleInputChange, 
  handleDriverChange,
  addDriver,
  removeDriver,
  errors
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Users className="w-5 h-5 mr-2 text-emerald-500" />
        Параметры водителей
      </h3>
      
      <div className="mb-4">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="multiDrive"
            checked={formData.multiDrive}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Без ограничения водителей (Мультидрайв)
          </span>
        </label>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          При выборе этой опции стоимость полиса увеличивается, но любой водитель с действующими правами может управлять ТС
        </p>
      </div>

      {!formData.multiDrive ? (
        // Параметры для ограниченного списка водителей
        <div>
          {formData.drivers.length === 0 ? (
            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-start">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-amber-500" />
              <div>
                Добавьте хотя бы одного водителя или выберите опцию "Без ограничения водителей"
              </div>
            </div>
          ) : (
            // Список водителей
            <div className="space-y-6">
              {formData.drivers.map((driver, index) => (
                <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Водитель #{index + 1}
                    </h4>
                    
                    {formData.drivers.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeDriver(index)}
                        variant="ghost"
                        size="sm"
                        icon={<UserX className="w-4 h-4 text-red-500" />}
                      >
                        Удалить
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Возраст водителя */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Возраст (лет)
                      </label>
                      <input
                        type="number"
                        value={driver.age}
                        onChange={(e) => handleDriverChange(index, 'age', e.target.value)}
                        min="18"
                        max="90"
                        className={`form-input w-full ${errors.driverErrors && errors.driverErrors[`${index}_age`] ? 'input-error' : ''}`}
                        placeholder="Введите возраст"
                      />
                      {errors.driverErrors && errors.driverErrors[`${index}_age`] && (
                        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                          {errors.driverErrors[`${index}_age`]}
                        </p>
                      )}
                    </div>
                    
                    {/* Стаж вождения */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Стаж вождения (лет)
                      </label>
                      <input
                        type="number"
                        value={driver.experience}
                        onChange={(e) => handleDriverChange(index, 'experience', e.target.value)}
                        min="0"
                        max="70"
                        className={`form-input w-full ${errors.driverErrors && errors.driverErrors[`${index}_experience`] ? 'input-error' : ''}`}
                        placeholder="Введите стаж"
                      />
                      {errors.driverErrors && errors.driverErrors[`${index}_experience`] && (
                        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                          {errors.driverErrors[`${index}_experience`]}
                        </p>
                      )}
                    </div>
                    
                    {/* Безаварийная езда */}
                    <div className="col-span-full">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={driver.noAccidents}
                          onChange={(e) => handleDriverChange(index, 'noAccidents', e.target.checked)}
                          className="form-checkbox"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Без аварий за последний год
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Кнопка добавления водителя */}
              <div className="mt-2">
                <Button
                  type="button"
                  onClick={addDriver}
                  variant="outline"
                  size="sm"
                  icon={<UserPlus className="w-4 h-4" />}
                >
                  Добавить водителя
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Информация для мультидрайва
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
          <div className="flex items-start">
            <Users className="w-5 h-5 mr-3 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                Что такое "Мультидрайв"?
              </h4>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                При выборе этой опции управлять автомобилем могут любые водители с действующими правами 
                соответствующей категории. Стоимость полиса увеличивается примерно на 94% от базовой ставки.
              </p>
            </div>
          </div>
        </div>
      )}

      {!formData.multiDrive && formData.drivers.length === 0 && errors.drivers && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
          {errors.drivers}
        </p>
      )}

      {/* Период использования ТС */}
      <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
        <label htmlFor="usagePeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Clock className="w-4 h-4 mr-2 text-emerald-500" />
          Период использования (месяцев)
        </label>
        <div className="flex items-center">
          <input
            type="range"
            id="usagePeriod"
            name="usagePeriod"
            min="3"
            max="12"
            step="1"
            value={formData.usagePeriod}
            onChange={handleInputChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          <span className="ml-3 font-medium text-gray-900 dark:text-white min-w-[30px]">
            {formData.usagePeriod}
          </span>
        </div>
        {errors.usagePeriod && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.usagePeriod}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Минимальный период страхования - 3 месяца, максимальный - 12 месяцев
        </p>
      </div>
    </div>
  );
};

export default DriverParametersSection;